import { createContext, useContext, useState, useEffect } from 'react';
import { customerAuthService } from '../services/customerAuthService';

const CustomerAuthContext = createContext(null);

export const useCustomerAuth = () => {
  const context = useContext(CustomerAuthContext);
  if (!context) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};

export const CustomerAuthProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const checkAuth = async () => {
    const token = localStorage.getItem('customerToken');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await customerAuthService.getProfile();
      if (response.success) {
        setCustomer(response.data);
      } else {
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerData');
      }
    } catch (err) {
      localStorage.removeItem('customerToken');
      localStorage.removeItem('customerData');
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  const login = async (phone, otp) => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerAuthService.verifyOtp(phone, otp);
      if (response.success) {
        setCustomer(response.data.customer);
        return response;
      } else {
        setError(response.message || 'Login failed');
        return response;
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithFirebase = async (idToken, phone) => {
    setLoading(true);
    setError(null);
    try {
      const response = await customerAuthService.loginWithFirebase(idToken, phone);
      if (response.success) {
        setCustomer(response.data.customer);
        return response;
      } else {
        setError(response.message || 'Login failed');
        return response;
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await customerAuthService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setCustomer(null);
      setLoading(false);
    }
  };

  const value = {
    customer,
    loading,
    error,
    login,
    loginWithFirebase,
    logout,
    checkAuth,
    setError,
  };

  return (
    <CustomerAuthContext.Provider value={value}>
      {children}
    </CustomerAuthContext.Provider>
  );
};

export default CustomerAuthContext;
